
import React from 'react';
import { useCookieConsent, CookieCategory } from './CookieConsentProvider';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Check, X } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const CookiePreferences: React.FC = () => {
  const { 
    cookiePreferences, 
    updateCookiePreferences, 
    acceptAllCookies, 
    rejectNonEssentialCookies,
    setShowPreferences
  } = useCookieConsent();

  const handleToggle = (category: CookieCategory) => {
    // Don't allow toggling necessary cookies
    if (category === 'necessary') return;
    
    // Update preference but don't hide the dialog
    updateCookiePreferences({ 
      [category]: !cookiePreferences[category] 
    });
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-medium text-gray-700">Cookie-voorkeuren</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowPreferences(false)}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Sluiten</span>
        </Button>
      </div>
      
      <p className="text-sm text-gray-500 mb-4">
        We gebruiken cookies om je surfervaring te verbeteren, analyse van websitegebruik mogelijk te maken en relevante marketinginhoud te tonen.
      </p>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="font-medium text-sm">Noodzakelijke cookies</p>
            <p className="text-xs text-gray-500">
              Deze cookies zijn nodig voor de werking van de website
            </p>
          </div>
          <Switch 
            checked={cookiePreferences.necessary} 
            disabled={true} 
            className="opacity-60 data-[state=checked]:bg-brand-blue"
          />
        </div>
        
        <Separator />
        
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="font-medium text-sm">Analytische cookies</p>
            <p className="text-xs text-gray-500">
              Verzamelen anonieme gegevens over het gebruik van de website
            </p>
          </div>
          <Switch 
            checked={cookiePreferences.analytics} 
            onCheckedChange={() => handleToggle('analytics')}
            className="data-[state=checked]:bg-brand-blue"
          />
        </div>
        
        <Separator />
        
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="font-medium text-sm">Marketing cookies</p>
            <p className="text-xs text-gray-500">
              Helpen bij het tonen van relevante advertenties
            </p>
          </div>
          <Switch 
            checked={cookiePreferences.marketing} 
            onCheckedChange={() => handleToggle('marketing')}
            className="data-[state=checked]:bg-brand-blue"
          />
        </div>
      </div>
      
      <div className="flex justify-end gap-2 mt-4">
        <Button 
          variant="outline" 
          size="sm"
          onClick={rejectNonEssentialCookies}
        >
          Weigeren
        </Button>
        <Button 
          variant="default" 
          size="sm" 
          onClick={acceptAllCookies}
        >
          Accepteren
        </Button>
      </div>
    </div>
  );
};

export default CookiePreferences;
